import { format as formatValue } from '@/modules/shared/utils/v-table'

type Deps = {
  entityId: string
}

export function useTransactionColumns({ entityId }: Deps) {
  const isCurrentEntity = (t: any) => String(t.investor_type) === 'InvestorSet' && String(t.investor_id) === entityId

  const columns = [
    {
      key: 'id',
      label: 'ID',
      type: 'integer' as const,
      hidden: true,
    },
    {
      key: 'type',
      label: 'Type',
      type: 'string' as const,
      align: 'left' as const,
      aggregate: (items: any[]) => items[0].display_type ?? items[0].type,
      flatten: (item: any) => item.display_type ?? item.type,
      subrow: null,
    },
    {
      key: 'commitment_id',
      label: 'Commitment ID',
      type: 'string' as const,
      align: 'center' as const,
      hidden: true,
    },
    { key: 'investor_id', label: 'Investor ID', type: 'integer' as const, hidden: true },
    { key: 'investor_type', label: 'Investor Type', type: 'string' as const, hidden: true },
    { key: 'investor_sequence', label: 'Investor Sequence', type: 'string' as const, hidden: true },
    {
      key: 'tax_investor_name',
      label: 'Tax Investor Name',
      type: 'string' as const,
      align: 'left' as const,
      hidden: true,
    },
    {
      key: 'company',
      label: 'Company',
      type: 'string' as const,
      align: 'left' as const,
      hidden: true,
    },
    {
      key: 'investor_name',
      label: 'Investor',
      type: 'string' as const,
      aggregate: (items: any[]) => items.filter((i) => i.investor_id && !isCurrentEntity(i)).length,
      align: 'left' as const,
    },
    {
      key: 'amount',
      label: 'Amount',
      type: 'currency' as const,
      aggregate: (items: any[]) => items.reduce((sum, item) => sum + item.amount, 0),
    },
    { key: 'date', label: 'Date', type: 'date' as const },
    {
      key: 'status',
      label: 'Status',
      type: 'string' as const,
      aggregate: (items: any[]) => {
        if (items[0]?.type === 'portfolio_distribution') {
          return { kind: 'distribute', pdaId: items[0].id }
        }
        if (items[0].display_type === 'scheduled_call') {
          return { kind: 'pending' }
        }
        if (items[0].type === 'capital_call') {
          const paid = items.filter((i) => i.status === 'paid').length
          return { kind: 'paid_count', paid, total: items.length }
        }
        if (items[0].type === 'distribution') {
          const paid = items.filter((i) => i.status !== 'unpaid').length
          return { kind: 'paid_count', paid, total: items.length }
        }
        return { kind: 'allocated' }
      },
      flatten: (item: any) => {
        if (item.type === 'portfolio_distribution') {
          return { kind: 'distribute', pdaId: item.id }
        }
        if (item.type === 'capital_call') {
          return {
            kind: 'capital_call_modal',
            callId: item.id,
            invoiceId: item.invoice_id,
            status: item.status,
            isScheduled: item.display_type === 'scheduled_call',
          }
        }
        if (item.type === 'distribution') {
          return {
            kind: 'distribution_modal',
            distributionId: String(item.id),
            status: item.status,
          }
        }
        return { kind: 'allocated' }
      },
    },
    {
      key: 'payment_status_marked_at',
      label: 'Paid At',
      type: 'string' as const,
      aggregate: null,
      flatten: (item: any) =>
        item.payment_status_marked_at ? formatValue(item.payment_status_marked_at, 'date') : '--',
    },
  ]

  return { columns }
}
